import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    DialogButtonComponent,
    DialogButtonsComponent,
    DialogComponent,
    InputSearchComponent,
    LoaderComponent,
    LoadMoreComponent,
    ScrollTrackerDirective,
    ScrollTrackerEvent,
    SeparatorComponent,
} from '@interacta-shared/ui';
import {
    emptyInputSelectSearchEvent,
    emptyPaginatedList,
    getNextPageToken,
    getRandomColor,
    InputSelectSearchEvent,
    PaginatedList,
} from '@interacta-shared/util';

import { I18nPluralPipe, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { MemberListPlaceholderComponent } from '@interacta-shared/ui-member';
import {
    fetchNextSearchPaginated,
    IUsersGroup,
} from '@interacta-shared/util-caronte';
import { focusFirstNewChild } from '@interacta-shared/util-common';
import { Member } from '@interacta-shared/util-member';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, filter } from 'rxjs';
import { MemberAdminComponent } from '../member-admin/member-admin.component';
import { MemberComponent } from '../member/member.component';
import { UsersGroupComponent } from '../users-group/users-group.component';
import { GroupInfoDialogService } from './group-info-dialog.service';

@Component({
    imports: [
        DialogComponent,
        DialogButtonComponent,
        DialogButtonsComponent,
        SeparatorComponent,
        LoaderComponent,
        InputSearchComponent,
        LoadMoreComponent,
        MemberListPlaceholderComponent,
        ScrollTrackerDirective,
        TranslatePipe,
        NgTemplateOutlet,
        I18nPluralPipe,
        UsersGroupComponent,
        MemberComponent,
        MemberAdminComponent,
        MatTooltip,
    ],
    selector: 'interacta-group-info-dialog',
    templateUrl: './group-info-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupInfoDialogComponent implements OnInit {
    private readonly groupInfoDialogService = inject(GroupInfoDialogService);
    private readonly destroyRef = inject(DestroyRef);

    readonly MEMBERS_TO_LOAD_AT_START = 8;
    readonly N_MEMBERS_TO_SHOW = 20;
    readonly getRandomColor = getRandomColor;

    readonly data = computed(() => this.groupInfoDialogService.data());
    readonly group = computed(() => this.data()?.group);

    readonly search = new FormControl<string>('', {
        nonNullable: true,
    });

    readonly members = signal<PaginatedList<Member>>(emptyPaginatedList());
    readonly remainingMembers = signal(0);
    readonly showMoreMembers = signal(false);

    // permission
    readonly isAdmin = computed(() =>
        this.groupInfoDialogService.showAdminInfo(),
    );
    readonly showEditButton = computed(
        () => this.isAdmin() && this.groupInfoDialogService.manageUsers(),
    );

    private changes?: MutationObserver;

    constructor() {
        this.destroyRef.onDestroy(() => this.changes?.disconnect());

        toObservable(this.data)
            .pipe(
                filter(({ isOpen }) => isOpen),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                this.showMoreMembers.set(false);
                this.fetchMembers(
                    emptyInputSelectSearchEvent(),
                    this.MEMBERS_TO_LOAD_AT_START,
                );
            });
    }

    ngOnInit(): void {
        this.search.valueChanges
            .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
            .subscribe((text) =>
                this.fetchMembers(
                    { text: text, nextPageToken: null },
                    this.N_MEMBERS_TO_SHOW,
                ),
            );
    }

    fetchMembers(inputFilters: InputSelectSearchEvent, pageSize: number): void {
        const group = this.group();
        if (!group) return;

        fetchNextSearchPaginated({
            inputFilters,
            currentFilters: {
                groupId: group.id,
            },
            list: this.members,
            fetchList: this.groupInfoDialogService.fetchMembers.bind(
                this.groupInfoDialogService,
            ),
            pageSize,
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!inputFilters.nextPageToken) {
                    this.remainingMembers.set(
                        this.members().totalCount - pageSize,
                    );
                }
            });
    }

    onScroll(
        event: ScrollTrackerEvent,
        paginatedList: PaginatedList<unknown>,
    ): void {
        if (event?.thresholdReached && this.showMoreMembers()) {
            this.fetchNextMembers(paginatedList);
        }
    }

    fetchNextMembers(
        paginatedList: PaginatedList<unknown>,
        focusNext?: HTMLElement,
    ): void {
        if (
            paginatedList.nextPageToken.tag === 'regularLoading' &&
            !paginatedList.isFetching
        ) {
            if (focusNext) {
                this.changes = focusFirstNewChild({
                    container: focusNext,
                    currentChildCount: paginatedList.list.length,
                    pendingObserver: this.changes,
                });
            }

            this.fetchMembers(
                {
                    text: this.search.value,
                    nextPageToken: getNextPageToken(
                        paginatedList.nextPageToken,
                    ),
                },
                this.N_MEMBERS_TO_SHOW,
            );
        }
    }

    loadMoreMembers(): void {
        this.showMoreMembers.set(true);
        this.fetchMembers(
            { text: '', nextPageToken: null },
            this.N_MEMBERS_TO_SHOW,
        );
    }

    edit(groupId: IUsersGroup['id']): void {
        this.groupInfoDialogService.goToEditGroup(groupId);
    }

    close(): void {
        this.search.reset('', { emitEvent: false });
        this.groupInfoDialogService.closeDialog();
    }
}
